// The typed API client. Types come from openapi.yaml (src/api/schema.gen.ts, generated by
// `npm run gen`, run before every dev/build/typecheck/test); never hand-write a request shape.
// Every API call in the app goes through `api` below.
import createClient from 'openapi-fetch';
import type { components, paths } from './schema.gen';
import { signInProvider, type SignInProvider } from '../lib/signin';

type S = components['schemas'];
export type Me = S['Me'];
export type Workspace = S['Workspace'];
export type Board = S['Board'];
export type BoardRepo = Board['repos'][number];
export type Card = S['Card'];
export type ColumnKey = S['ColumnKey'];
export type ViewKey = S['ViewKey'];
export type Column = S['Column'];
export type PullSummary = S['PullSummary'];
export type IssueSummary = S['IssueSummary'];
export type Checks = S['Checks'];
export type Record = S['Record'];
export type Tab = S['Tab'];
export type Field = S['Field'];
export type FileDoc = S['FileDoc'];
export type ActivityEvent = S['ActivityEvent'];
export type MetaRepo = S['MetaRepo'];
export type Member = S['Member'];
export type Invite = S['Invite'];
export type Role = S['Role'];
export type Sync = S['Sync'];
export type Unreported = S['Unreported'];
export type SearchResult = S['SearchResult'];
export type ApiError = S['Error'];
export type GitHubLink = S['GitHubLink'];
export type MergeGuard = S['MergeGuard'];
export type MergeResult = S['MergeResult'];
export type Fleet = S['Fleet'];
export type FleetHost = S['FleetHost'];
export type FleetSession = S['FleetSession'];
export type FleetRunner = S['FleetRunner'];
export type FleetCommand = S['FleetCommand'];
export type FleetMachinesFile = S['FleetMachinesFile'];
export type FleetMachinesFileDoc = S['FleetMachinesFileDoc'];
export type FleetGitHub = S['FleetGitHub'];
export type FleetWorking = S['FleetWorking'];
export type FleetHostToken = S['FleetHostToken'];
export type CiRun = S['CiRun'];
export type CiRunDetail = S['CiRunDetail'];
export type CiJob = S['CiJob'];
export type CiStep = S['CiStep'];
export type CiResult = S['CiResult'];
export type CiRepo = S['CiRepo'];
export type CiTrend = S['CiTrend'];
export type CiTrends = S['CiTrends'];
export type AlertState = S['AlertState'];
export type AlertSettings = S['AlertSettings'];
export type AlertChannels = S['AlertChannels'];
export type AlertChannel = S['AlertChannel'];
export type AlertIncident = S['AlertIncident'];
export type PushDevice = S['PushDevice'];

export const api = createClient<paths>({
  // Same origin; absolute so the client also runs where Request needs a full URL (tests).
  baseUrl: globalThis.location?.origin ?? '',
  credentials: 'same-origin',
  // Every request carries it; the API refuses a state-changing request without it (CSRF).
  headers: { 'X-Requested-With': 'shipyard' },
  // Looked up per call, so tests can replace globalThis.fetch.
  fetch: (request: Request) => globalThis.fetch(request),
});

/** End the board session (POST /auth/signout; the client's default header covers CSRF). */
export async function signOut(): Promise<void> {
  await api.POST('/auth/signout');
}

/** The sign-in URL that returns to `next` (a path on this host). */
export function signInHref(next = '/', provider: SignInProvider = signInProvider()): string {
  return `/auth/${provider}/start?next=${encodeURIComponent(next)}`;
}

/** Link the user's GitHub account (for merging), returning to `next` (a path on this host). */
export function linkGitHubHref(next = '/'): string {
  return `/auth/github/start?next=${encodeURIComponent(next)}`;
}

/**
 * A readable message for a failed call. The API's own `message` wins (it names the reason, e.g.
 * an unsupported repos.yaml format); otherwise one line per status.
 */
export function errorMessage(error: unknown, status?: number): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  if (status === 401) return 'Your session has ended. Sign in again.';
  if (status === 403) return 'Only workspace owners can do that.';
  if (status === 404) return 'Not found.';
  if (status && status >= 500) return `The server could not answer (${status}).`;
  return 'The API could not be reached.';
}
